import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, SearchInput, Select, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal
} from '../../components/UI';
import api, { recordingsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlay, HiOutlineDownload, HiOutlineTrash, HiOutlineMicrophone
} from 'react-icons/hi';
import { format } from 'date-fns';

const RecordingsList = () => {
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  // Fetch audio URL with authentication when recording is selected
  useEffect(() => {
    if (selectedRecording) {
      const fetchAudio = async () => {
        try {
          const response = await api.get(`/recordings/file/${selectedRecording._id}`, {
            responseType: 'blob'
          });
          const url = URL.createObjectURL(response.data);
          setAudioUrl(url);
        } catch (error) {
          console.error('Failed to load audio:', error);
          toast.error('Failed to load recording');
        }
      };
      fetchAudio();
    } else {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
    }
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [selectedRecording?._id]);

  // Fetch recordings with filters
  const { data: recordingsData, isLoading, refetch } = useQuery({
    queryKey: ['recordings', page, userFilter, statusFilter, search],
    queryFn: async () => {
      const params = { page, limit: 20 };
      if (userFilter) params.userId = userFilter;
      if (statusFilter) params.uploadStatus = statusFilter;
      if (search) params.search = search;
      const response = await recordingsAPI.getAll(params);
      return response.data || {};
    },
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['recordingStats'],
    queryFn: async () => {
      const response = await recordingsAPI.getAll({ limit: 1 });
      return response.data?.stats || {};
    },
  });

  const recordings = recordingsData?.data || [];
  const pagination = recordingsData?.pagination || {};
  const totalRecordings = pagination.total || 0;
  const totalSize = stats?.totalSize || 0;

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle delete recording
  const handleDeleteRecording = async () => {
    if (!recordingToDelete) return;

    try {
      await recordingsAPI.delete(recordingToDelete._id);
      toast.success('Recording deleted successfully');
      setShowDeleteModal(false);
      setRecordingToDelete(null);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete recording');
    }
  };

  // Export recordings list
  const handleExport = () => {
    if (recordings.length === 0) {
      toast.error('No recordings to export');
      return;
    }

    const csvRows = [];
    csvRows.push('Phone Number,Lead Name,User,Call Type,Status,Duration,File Size,Created At');

    recordings.forEach((rec) => {
      csvRows.push([
        `"${rec.phoneNumber || ''}"`,
        `"${rec.leadId?.name || 'Unknown'}"`,
        `"${rec.userId?.firstName || ''} ${rec.userId?.lastName || ''}"`,
        `"${rec.callType || 'outgoing'}"`,
        `"${rec.callStatus || 'connected'}"`,
        `"${formatDuration(rec.duration)}"`,
        `"${formatFileSize(rec.fileSize)}"`,
        `"${rec.createdAt ? format(new Date(rec.createdAt), 'yyyy-MM-dd HH:mm') : ''}"`
      ].join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `recordings-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    toast.success('Export successful');
  };

  // Get upload status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'uploaded':
        return <Badge variant="success" size="sm">Uploaded</Badge>;
      case 'pending':
        return <Badge variant="warning" size="sm">Pending</Badge>;
      case 'failed':
        return <Badge variant="danger" size="sm">Failed</Badge>;
      default:
        return <Badge variant="default" size="sm">{status}</Badge>;
    }
  };

  // Get call type badge
  const getCallTypeBadge = (callType) => {
    switch (callType) {
      case 'incoming':
        return <Badge variant="info" size="sm">Incoming</Badge>;
      case 'missed':
        return <Badge variant="danger" size="sm">Missed</Badge>;
      default:
        return <Badge variant="primary" size="sm">Outgoing</Badge>;
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Recordings</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Manage call recordings (auto-deleted after 30 days)
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<HiOutlineDownload className="w-4 h-4" />}
            onClick={handleExport}
          >
            <span className="hidden sm:inline">Export</span>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center">
            <p className="text-xs text-gray-500">Total Recordings</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-gray-900">
              {pagination.total || 0}
            </p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-blue-50">
            <p className="text-xs text-blue-600">Total Size</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-blue-600">
              {formatFileSize(totalSize)}
            </p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-green-50">
            <p className="text-xs text-green-600">Uploaded</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-green-600">
              {recordings.filter(r => r.uploadStatus === 'uploaded').length}
            </p>
          </Card>
          <Card padding="p-2.5 sm:p-3 lg:p-4" className="text-center bg-yellow-50">
            <p className="text-xs text-yellow-600">Pending</p>
            <p className="text-base sm:text-lg lg:text-2xl font-bold text-yellow-600">
              {recordings.filter(r => r.uploadStatus === 'pending').length}
            </p>
          </Card>
        </div>

        {/* Filters */}
        <Card padding="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search by phone or lead name..."
              />
            </div>
            <div className="flex gap-2">
              <Select
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'uploaded', label: 'Uploaded' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'failed', label: 'Failed' },
                ]}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="min-w-[120px]"
              />
            </div>
          </div>
        </Card>

        {/* Recordings List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : recordings.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineMicrophone className="w-8 h-8 text-gray-400" />}
              title="No recordings found"
              description="Call recordings will appear here after calls are made"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header className="min-w-[120px]">Phone</TableCell>
                    <TableCell header className="min-w-[150px]">Lead</TableCell>
                    <TableCell header className="min-w-[100px]">User</TableCell>
                    <TableCell header className="min-w-[100px]">Type</TableCell>
                    <TableCell header className="min-w-[100px]">Status</TableCell>
                    <TableCell header className="min-w-[80px]">Duration</TableCell>
                    <TableCell header className="min-w-[80px]">Size</TableCell>
                    <TableCell header className="min-w-[100px]">Date</TableCell>
                    <TableCell header className="min-w-[120px]">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recordings.map((rec) => (
                    <TableRow key={rec._id}>
                      <TableCell>
                        <span className="font-medium text-gray-900 whitespace-nowrap">
                          {rec.phoneNumber}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 truncate max-w-[150px] block">
                          {rec.leadId?.name || 'Unknown'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 text-sm">
                          {rec.userId?.firstName} {rec.userId?.lastName}
                        </span>
                      </TableCell>
                      <TableCell>
                        {getCallTypeBadge(rec.callType)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(rec.uploadStatus)}
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 whitespace-nowrap">
                          {formatDuration(rec.duration)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 whitespace-nowrap">
                          {formatFileSize(rec.fileSize)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-500 text-sm whitespace-nowrap">
                          {rec.createdAt ? format(new Date(rec.createdAt), 'MMM d, HH:mm') : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                            onClick={() => setSelectedRecording(rec)}
                            title="Play Recording"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlineDownload className="w-4 h-4 text-blue-600" />}
                            onClick={async () => {
                              try {
                                const response = await api.get(`/recordings/file/${rec._id}`, {
                                  responseType: 'blob'
                                });
                                const url = URL.createObjectURL(response.data);
                                const link = document.createElement('a');
                                link.href = url;
                                link.download = rec.fileName || `recording-${rec._id}.mp3`;
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                URL.revokeObjectURL(url);
                                toast.success('Download started');
                              } catch (error) {
                                toast.error('Failed to download recording');
                              }
                            }}
                            title="Download"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5"
                            icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                            onClick={() => {
                              setRecordingToDelete(rec);
                              setShowDeleteModal(true);
                            }}
                            title="Delete"
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {recordings.map((rec) => (
                <div key={rec._id} className="p-3 sm:p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{rec.phoneNumber}</p>
                      <p className="text-xs sm:text-sm text-gray-500 truncate">
                        {rec.leadId?.name || 'Unknown Lead'}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {getCallTypeBadge(rec.callType)}
                      {getStatusBadge(rec.uploadStatus)}
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                    <span>{rec.userId?.firstName} {rec.userId?.lastName}</span>
                    <span>{formatDuration(rec.duration)} • {formatFileSize(rec.fileSize)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                    <span>{rec.createdAt ? format(new Date(rec.createdAt), 'MMM d, yyyy HH:mm') : '-'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 pt-2 border-t border-gray-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 flex-1"
                      icon={<HiOutlinePlay className="w-4 h-4 text-primary-600" />}
                      onClick={() => setSelectedRecording(rec)}
                    >
                      <span className="text-xs">Play</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2 flex-1"
                      icon={<HiOutlineDownload className="w-4 h-4 text-blue-600" />}
                      onClick={async () => {
                        try {
                          const response = await api.get(`/recordings/file/${rec._id}`, {
                            responseType: 'blob'
                          });
                          const url = URL.createObjectURL(response.data);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = rec.fileName || `recording-${rec._id}.mp3`;
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          URL.revokeObjectURL(url);
                          toast.success('Download started');
                        } catch (error) {
                          toast.error('Failed to download recording');
                        }
                      }}
                    >
                      <span className="text-xs">Download</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                      onClick={() => {
                        setRecordingToDelete(rec);
                        setShowDeleteModal(true);
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between p-3 sm:p-4 border-t border-gray-100">
                <span className="text-xs sm:text-sm text-gray-500">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-1.5 sm:gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Recording Modal */}
        <Modal
          isOpen={!!selectedRecording}
          onClose={() => setSelectedRecording(null)}
          title="Call Recording"
          size="md"
        >
          {selectedRecording && (
            <div className="space-y-3 sm:space-y-4">
              <div className="bg-gray-50 p-3 sm:p-4 rounded-xl">
                <div className="flex items-start justify-between gap-2 mb-3 sm:mb-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">
                      {selectedRecording.phoneNumber}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 truncate">
                      {selectedRecording.leadId?.name || 'Unknown Lead'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5 sm:mt-1">
                      User: {selectedRecording.userId?.firstName} {selectedRecording.userId?.lastName}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {getCallTypeBadge(selectedRecording.callType)}
                    {getStatusBadge(selectedRecording.uploadStatus)}
                  </div>
                </div>
                <div className="text-xs sm:text-sm text-gray-500 mb-3">
                  {selectedRecording.createdAt
                    ? format(new Date(selectedRecording.createdAt), 'MMM d, yyyy h:mm a')
                    : '-'}
                  {' • '}
                  {formatDuration(selectedRecording.duration)}
                  {' • '}
                  {formatFileSize(selectedRecording.fileSize)}
                </div>
                {audioUrl ? (
                  <audio
                    controls
                    className="w-full"
                    src={audioUrl}
                  >
                    Your browser does not support the audio element.
                  </audio>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                    <span className="ml-2 text-gray-500 text-sm">Loading audio...</span>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  icon={<HiOutlineDownload className="w-4 h-4" />}
                  onClick={async () => {
                    try {
                      const response = await api.get(`/recordings/file/${selectedRecording._id}`, {
                        responseType: 'blob'
                      });
                      const url = URL.createObjectURL(response.data);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = selectedRecording.fileName || `recording-${selectedRecording._id}.mp3`;
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      URL.revokeObjectURL(url);
                      toast.success('Download started');
                    } catch (error) {
                      toast.error('Failed to download recording');
                    }
                  }}
                >
                  Download
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  icon={<HiOutlineTrash className="w-4 h-4" />}
                  onClick={() => {
                    setSelectedRecording(null);
                    setRecordingToDelete(selectedRecording);
                    setShowDeleteModal(true);
                  }}
                >
                  Delete
                </Button>
              </div>
            </div>
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setRecordingToDelete(null);
          }}
          title="Delete Recording"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this recording? This action cannot be undone.
            </p>
            {recordingToDelete && (
              <div className="bg-red-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-900">
                  {recordingToDelete.phoneNumber}
                </p>
                <p className="text-xs text-gray-500">
                  {recordingToDelete.leadId?.name || 'Unknown Lead'} • {formatDuration(recordingToDelete.duration)}
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowDeleteModal(false);
                  setRecordingToDelete(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteRecording}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default RecordingsList;